/**
 * Referral capture on the public marketing site.
 *
 * Two ways somebody tells us who sent them:
 *
 *   1. A LINK. A colleague shares `/waitlist?ref=JONATHAN` (the param works on
 *      any marketing page). The visitor types nothing and the attribution is
 *      exact — this is the only reason referrer codes exist at all.
 *   2. THE BOX. They heard about Melio in a conversation and type a name. That
 *      is resolved server-side against the `referrers` registry.
 *
 * WHY sessionStorage AND NOT THE FIRST-TOUCH COOKIE. First touch is a 180-day
 * marketing cookie gated on consent, and it is deliberately write-once: a
 * referral link followed by someone who already has a cookie would be dropped.
 * The referrer code is a different thing — it belongs to THIS visit and the
 * form at the end of it — so it lives in session storage, dies with the tab,
 * and never widens the cookie surface the consent banner governs. Same
 * reasoning (and the same storage) as rememberWaitlistRef in waitlistService.
 *
 * NOTHING HERE IS TRUSTED. The display name this module fetches is cosmetic
 * confirmation for the visitor. Attribution is decided by `joinWaitlist` from
 * the raw text, on the server, every time.
 *
 * FIREBASE IS IMPORTED LAZILY, INSIDE lookupReferrer. `src/firebase.ts` boots
 * the whole SDK at module scope, so a top-level import here would drag it into
 * every page that renders the footer form — and into every test that renders
 * one, which is why several of them already stub waitlistService by hand. The
 * lookup only ever runs for the minority of visitors who arrived on a `?ref=`
 * link, so deferring it is what it should have been regardless.
 */

import { normaliseReferrerCode, type ReferrerKind } from '../types/referrers';

/** `?ref=` — short because it gets pasted into messages by hand. */
export const REFERRAL_PARAM = 'ref';

const REFERRAL_STORAGE_KEY = 'melio_referrer_code';

export interface ResolvedReferrer {
  code: string;
  displayName: string;
  kind: ReferrerKind;
}

/**
 * Pull a referrer code out of a query string, validate its shape, and remember
 * it for the rest of the visit. Returns the stored code — the one from the URL
 * if there was one, otherwise whatever an earlier page in this visit stored.
 *
 * LAST LINK WINS, unlike first touch. If someone follows two colleagues' links
 * in one session, the one they acted on is the one they arrived through, and
 * pinning the earlier one would credit a colleague whose link they abandoned.
 */
export function captureReferralCode(search: string): string | null {
  if (!isBrowser()) return null;
  let code: string | null = null;
  try {
    code = normaliseReferrerCode(new URLSearchParams(search).get(REFERRAL_PARAM));
  } catch {
    code = null;
  }
  if (code) {
    try {
      sessionStorage.setItem(REFERRAL_STORAGE_KEY, code);
    } catch {
      // Storage blocked (private mode). The code still works on this page; it
      // just will not survive a navigation.
    }
    return code;
  }
  return readReferralCode();
}

export function readReferralCode(): string | null {
  if (!isBrowser()) return null;
  try {
    return normaliseReferrerCode(sessionStorage.getItem(REFERRAL_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function clearReferralCode(): void {
  if (!isBrowser()) return;
  try {
    sessionStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Look up the name behind a code so the form can say "Referred by Jonathan
 * Davies" instead of showing a raw code.
 *
 * `firestore.rules` allows a single-document `get` on an ACTIVE referrer and
 * denies `list`, so this cannot be used to enumerate the team. A miss returns
 * null and the form falls back to the plain box — an unrecognised code must
 * never block someone from joining.
 */
export async function lookupReferrer(code: string | null): Promise<ResolvedReferrer | null> {
  const normalised = normaliseReferrerCode(code);
  if (!normalised) return null;
  try {
    const [{ doc, getDoc }, { db }] = await Promise.all([
      import('firebase/firestore'),
      import('../firebase'),
    ]);
    const snap = await getDoc(doc(db, 'referrers', normalised));
    if (!snap.exists()) return null;
    const data = snap.data() as Record<string, unknown>;
    if (data.active !== true) return null;
    const displayName = typeof data.displayName === 'string' ? data.displayName : '';
    if (!displayName) return null;
    return {
      code: normalised,
      displayName,
      kind: (typeof data.kind === 'string' ? data.kind : 'staff') as ReferrerKind,
    };
  } catch (err) {
    console.warn('lookupReferrer failed', err);
    return null;
  }
}

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}
